<script lang="ts">
  /**
   * Which classes of airspace to draw, and how strongly.
   *
   * Lifted out of the heatmap so the flight map can have the same thing rather than a second
   * implementation of it. The two maps draw airspace for different reasons — one to see where a
   * flight went relative to it, one to see where the club flies — but the choice of *which*
   * airspace is the same choice, and it should not be made twice in two slightly different ways.
   *
   * The opacity slider exists because there is no one right value. Over southern Germany the
   * volumes stack three and four deep; enough to read a single boundary is enough to bury the
   * terrain underneath, and which of those matters depends on what the reader is doing.
   */
  import { AIRSPACE_GROUPS } from './airspace.ts';

  interface Props {
    /** Ids of the visible groups. Bindable; replaced wholesale rather than mutated. */
    groups: Set<string>;
    /** 0..1, applied to the extrusion fill. Bindable. */
    opacity: number;
    /** A line of explanation under the chips, or nothing. */
    hint?: string;
    /** `panel` floats over a map; `bar` sits in a page's own layout. */
    variant?: 'panel' | 'bar';
  }

  let { groups = $bindable(), opacity = $bindable(), hint = '', variant = 'bar' }: Props = $props();

  function toggle(id: string): void {
    // A new Set, not a mutation: `$state` tracks the reference, and mutating in place would not
    // re-run the effect that pushes the filter into the map.
    const next = new Set(groups);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    groups = next;
  }
</script>

<div class="airspace-filter" class:panel={variant === 'panel'}>
  <div class="chips">
    {#each AIRSPACE_GROUPS as g (g.id)}
      <button
        class="chip"
        class:on={groups.has(g.id)}
        style="--c:{g.colour}"
        onclick={() => toggle(g.id)}
        type="button"
        aria-pressed={groups.has(g.id)}
      >
        <span class="swatch" aria-hidden="true"></span>{g.label}
      </button>
    {/each}
  </div>

  <label class="opacity">
    <span class="faint">Deckkraft</span>
    <input
      type="range"
      min="0"
      max="60"
      value={Math.round(opacity * 100)}
      oninput={(e) => {
        opacity = Number((e.currentTarget as HTMLInputElement).value) / 100;
      }}
    />
    <span class="num">{Math.round(opacity * 100)} %</span>
  </label>

  {#if hint !== ''}
    <span class="hint">{hint}</span>
  {/if}
</div>

<style>
  .airspace-filter {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.4rem 0.75rem;
  }

  /* Over a map: the same translucent-over-blur pill language as the layer switcher above it. */
  .panel {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.35rem;
    max-width: 21rem;
    padding: 0.4rem 0.6rem 0.5rem;
    border-radius: var(--radius-sm);
    background: color-mix(in srgb, var(--bg-raised) 88%, transparent);
    border: 1px solid var(--line);
    backdrop-filter: blur(8px);
  }

  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.3rem;
  }

  .chip {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    border: 1px solid var(--line-strong);
    background: transparent;
    color: var(--text-dim);
    font: inherit;
    font-size: 0.72rem;
    padding: 0.2rem 0.5rem;
    border-radius: 999px;
    cursor: pointer;
    white-space: nowrap;
  }
  .chip.on {
    border-color: var(--c);
    color: var(--text);
    background: color-mix(in srgb, var(--c) 16%, transparent);
  }
  .swatch {
    width: 9px;
    height: 9px;
    border-radius: 2px;
    background: var(--c);
    opacity: 0.4;
    flex: none;
  }
  .chip.on .swatch {
    opacity: 1;
  }

  .opacity {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    font-size: 0.72rem;
  }
  .opacity input[type='range'] {
    width: 7rem;
    accent-color: var(--accent);
  }
  /* A 112 px slider is a mouse control. Dragged with a thumb it is about eight pixels per
     ten percent, so it gets the width instead of the row. */
  @media (pointer: coarse) {
    .opacity {
      width: 100%;
    }
    .opacity input[type='range'] {
      flex: 1;
      width: auto;
      min-width: 8rem;
      height: 2rem;
    }
  }
  .opacity .num {
    font-variant-numeric: tabular-nums;
    min-width: 2.6rem;
  }

  .hint {
    font-size: 0.7rem;
    color: var(--text-dim);
    max-width: 52ch;
    line-height: 1.4;
  }
</style>
